import type { AxisDomainItem } from 'recharts';
import { useEffect, useRef, useState } from 'react';

/** A recharts axis domain (the array forms of recharts' `AxisDomain`): a homogeneous string/number
 * array or a `[min, max]` pair, each end a concrete number or a token like `'dataMin'` / `'dataMax'`.
 * Only fully-numeric pairs are animated; anything else passes through. */
export type ChartAxisDomain = ReadonlyArray<string> | ReadonlyArray<number> | readonly [AxisDomainItem, AxisDomainItem];

/** Duration of the zoom tween. Long enough to read as motion, short enough not to feel sluggish. */
const ZOOM_ANIMATION_MS = 350;

const easeInOutCubic = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

const isNumericPair = (domain: ChartAxisDomain | undefined): domain is [number, number] =>
  Array.isArray(domain) &&
  domain.length === 2 &&
  typeof domain[0] === 'number' &&
  typeof domain[1] === 'number' &&
  Number.isFinite(domain[0]) &&
  Number.isFinite(domain[1]);

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Tween a chart's x-axis `domain` from its previous value to `target` over ~350ms so zooming the
 * timeline glides between windows instead of snapping. recharts applies a `domain` prop change
 * instantly on its own — feeding it these interpolated frames is the whole animation.
 *
 * Returns `target` unchanged (no animation) when either endpoint isn't a finite number — e.g. the
 * `['dataMin', 'dataMax']` auto-domain — on the first render (nothing to tween from), or when the user
 * prefers reduced motion. Charts sharing a domain each run their own tween off the same source and
 * target, so they stay in lockstep without extra coordination.
 */
export function useAnimatedDomain(target: ChartAxisDomain | undefined): ChartAxisDomain | undefined {
  const targetStart = target?.[0];
  const targetEnd = target?.[1];

  const [domain, setDomain] = useState(target);
  // The domain currently painted — the source of the next tween. A ref so mid-flight frames advance it
  // without re-triggering the effect (which keys only on the target endpoints).
  const currentRef = useRef(target);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const next: ChartAxisDomain | undefined =
      targetStart !== undefined && targetEnd !== undefined ? [targetStart, targetEnd] : undefined;
    const from = currentRef.current;

    // Nothing to interpolate between (auto-domain, first value, or reduced-motion) — jump straight there.
    if (!isNumericPair(next) || !isNumericPair(from) || prefersReducedMotion()) {
      currentRef.current = next;
      setDomain(next);
      return;
    }

    const [fromStart, fromEnd] = from;
    const [toStart, toEnd] = next;
    if (fromStart === toStart && fromEnd === toEnd) return;

    const startedAt = performance.now();
    const tick = (now: number): void => {
      const progress = Math.min(1, (now - startedAt) / ZOOM_ANIMATION_MS);
      const eased = easeInOutCubic(progress);
      const frame: [number, number] = [fromStart + (toStart - fromStart) * eased, fromEnd + (toEnd - fromEnd) * eased];
      currentRef.current = frame;
      setDomain(frame);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        currentRef.current = next;
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    return (): void => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [targetStart, targetEnd]);

  return domain;
}
